import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { APP_NAME, NOT_REFUNDABLE } from "@/config";
import { api } from "@/lib/api";
import { usd } from "@/components/common";
import { useFlags } from "@/lib/flags";

import PublicShell from "@/components/PublicShell";
import PlanCard from "@/components/PlanCard";

// /pricing: public, no sign-in. The plans and top-ups an admin has put on sale (GET /api/store/catalog);
// nothing here is hardcoded. Our payment provider reviews this page with /terms, /privacy and /refunds. With the AI
// wallet and seats (FEATURE_WALLET; design_handoff_team_roles/screens/Pricing.dc.html): seats on each plan, a Custom
// card, and the extras and refund lines.
// a custom plan (TEAM_ROLES_PLAN.md 7.10): set up by hand, on Crewbench's server
function CustomCard() {
  const rows = [["Server", "Your size, or a dedicated machine"], ["Seats and limits", "As agreed"], ["AI", "Your own API keys"], ["Investors", "Unlimited"]];
  return (
    <div className="bg-card rounded-[10px] p-[18px] flex flex-col gap-2.5" data-testid="pricing-plan-custom">
      <div className="flex justify-between items-baseline gap-2"><span className="font-display text-[15px] tracking-[0.02em] font-semibold">Custom</span></div>
      <span className="text-[24px] leading-7">Let's talk</span>
      <ul className="m-0 p-0 list-none flex flex-col gap-1.5 text-sm text-gr-ink-secondary">
        {rows.map(([k, v]) => <li key={k} className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="text-end">{v}</span></li>)}
      </ul>
      <span className="text-sm text-muted-foreground [text-wrap:pretty]">Still on {APP_NAME}'s server: founders never get root, so the privacy promise holds.</span>
      <a href="mailto:support@sidelab.dev?subject=A%20custom%20plan" className="mt-auto inline-flex justify-center items-center h-8 px-3 rounded-md border border-gr-control-border font-semibold hover:bg-gr-hover">Contact us</a>
    </div>
  );
}

export default function Pricing() {
  const wallet = useFlags().wallet;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["store-catalog"], queryFn: async () => (await api.get("/store/catalog")).data, retry: false,
  });
  useEffect(() => {
    document.title = `Pricing · ${APP_NAME}`;
    return () => { document.title = APP_NAME; };
  }, []);
  const plans = data?.plans || [];
  const topups = data?.topups || [];
  const empty = isError || (data && !plans.length && !topups.length);

  return (
    <PublicShell wide testid="pricing">
      <h1 className="font-display font-semibold text-[20px] leading-[1.2] tracking-[0.02em] sm:text-h1 mb-5">
        {wallet ? <><span className="block font-normal text-muted-foreground">One plan per company.</span>Your own staff, hired professionals and investors, on one private server.</> : "Pricing"}
      </h1>
      {wallet ? (
        <p className="text-lead font-light text-gr-ink-secondary max-w-2xl mb-14">Every plan includes a workspace server, AI credits each month, team member seats and independent professionals. Investors are free on every plan.</p>
      ) : <p className="text-lead font-light text-gr-ink-secondary max-w-2xl mb-14">
        Private AI coding sessions for your team, with Claude Agent (Anthropic) and Codex (OpenAI). Use your own
        API keys and server, or let {APP_NAME} supply both: a plan includes a workspace server and monthly
        credits, and top-ups add credits whenever you need them.
      </p>}
      {isLoading ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" aria-label="Loading" />
        : empty ? <p className="text-muted-foreground" data-testid="pricing-soon">Plans are coming soon.</p> : (
        <>
          {plans.length > 0 && (
            <section className="mb-12" aria-labelledby="pricing-plans">
              <h2 id="pricing-plans" className="font-display text-h4 font-semibold mb-1">Plans</h2>
              <p className="text-muted-foreground mb-6">Monthly, renewing until you cancel. {wallet ? "Credits never expire." : "A plan's credits reset at each renewal."}</p>
              <div className={wallet ? "grid md:grid-cols-2 xl:grid-cols-4 gap-6" : "grid md:grid-cols-3 gap-6"}>
                {plans.map((p) => (
                  <PlanCard key={p.id} plan={p} data-testid={`pricing-plan-${p.id}`}>
                    {wallet && <Link to="/signup" className="flex justify-center items-center h-8 px-3 rounded-md bg-foreground text-background font-semibold hover:opacity-80">Choose {p.name}</Link>}
                  </PlanCard>
                ))}
                {wallet && <CustomCard />}
              </div>
              {wallet && (
                <div className="mt-6 px-5 py-4 rounded-[10px] border flex flex-col gap-1.5 text-sm" data-testid="pricing-extras">
                  <p className="m-0">Extra team member seats and independent professional slots: $10 a month each, with a $9 AI credit bonus in your wallet.</p>
                  <p className="m-0 text-muted-foreground">Credits never expire and are refundable any time. {NOT_REFUNDABLE}</p>
                </div>
              )}
            </section>
          )}
          {topups.length > 0 && (
            <section className="mb-12" aria-labelledby="pricing-topups">
              <h2 id="pricing-topups" className="font-display text-h4 font-semibold mb-1">Credit top-ups</h2>
              <p className="text-muted-foreground mb-6">{wallet ? "One-time purchases into your AI wallet. They never expire." : "One-time purchases, valid 12 months after your latest top-up."}</p>
              <div className="flex flex-wrap gap-4">
                {topups.map((t) => (
                  <div key={t.id} className="rounded-md bg-gr-surface-2 px-7 py-2 leading-6" data-testid={`pricing-topup-${t.id}`}>
                    <span className="font-semibold">{t.display_price}</span>
                    <span className="text-muted-foreground"> · {usd(t.credits_usd, 0)} of credits</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
      <div className="text-sm text-muted-foreground space-y-2 border-t pt-8">
        <p>{wallet ? "Prices in US dollars. AI credits pay for usage at the provider's price plus our markup; you can also use your own API keys."
          : "Credits pay for AI usage at the provider's price plus our markup, shown in the app before you start a session."}</p>
        <p>
          Payments are handled by Lemon Squeezy, our reseller and merchant of record. Taxes such as VAT are added
          where they apply. See the <Link to="/refunds" className="underline underline-offset-2 hover:text-foreground">refund policy</Link> and
          the <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">Terms of Service</Link>.
        </p>
        <p><Link to="/signup" className="underline underline-offset-2 hover:text-foreground">Create an account</Link> to get started.</p>
      </div>
    </PublicShell>
  );
}
